import { differenceInCalendarDays } from 'date-fns';

import type { DailyInvoicingDatum } from '../types';

export type DailyInvoicingQuery = {
  /** ISO yyyy-MM-dd, inclusive. */
  from: string;
  /** ISO yyyy-MM-dd, inclusive. */
  to: string;
};

/** Shaped like a future API response so a real fetch can drop in without changing call sites. */
export type DailyInvoicingResponse = {
  data: DailyInvoicingDatum[];
};

function isWeekendDate(date: Date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

/** Local calendar date, not `toISOString()` — that converts to UTC and can shift the day. */
function toISODate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Deterministic mock series so the chart looks the same on every render. */
export function generateDailyInvoicingData({
  from,
  to,
}: DailyInvoicingQuery): DailyInvoicingResponse {
  const start = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  const days = Math.max(0, differenceInCalendarDays(end, start) + 1);
  const data: DailyInvoicingDatum[] = [];

  for (let index = 0; index < days; index += 1) {
    const date = new Date(start);
    date.setDate(start.getDate() + index);

    // Days since epoch, not `index` — so ranges of equal length (e.g. "this
    // month" vs "last month" when both happen to be 30 days) still produce
    // different values per date.
    const dayOffset = Math.floor(date.getTime() / 86_400_000);
    const wave = Math.sin(dayOffset * 0.45) * 35_000 + Math.sin(dayOffset * 0.13) * 20_000;
    const value = isWeekendDate(date)
      ? 0
      : Math.max(20_000, Math.round(140_000 + wave));

    data.push({ date: toISODate(date), value });
  }

  return { data };
}
